import { FunctionComponent, SVGProps } from "react";

import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faDatabase } from "@fortawesome/pro-regular-svg-icons";

import { ListSourceType } from "@/enum/list.enum";

import { iconVariantClasses } from "../pages/ListCreate/components/CatalogCard";
import {
	CatalogCategory,
	CatalogIconVariant,
	getCatalogTiles,
} from "../pages/ListCreate/config/tileCatalog";
import { createNewListSettings } from "../pages/ListCreate/config/tileConfig";

/**
 * Source-type filter catalog for the Lead List Overview.
 *
 * The List Overview "Source" filter and the Create-New-List catalog describe the *same* set of
 * acquisition sources, so this registry derives its category, icon, icon colour and search keywords
 * straight from the Create-flow tiles ({@link createNewListSettings} / {@link getCatalogTiles}).
 * That keeps the two surfaces visually and semantically in sync — the only filter-specific bits are
 * the (deliberately concise) label/description i18n keys defined per source below.
 *
 * Granularity note: the filter works at {@link ListSourceType} level, while the Create catalog has
 * one tile per source *sub-type*. We therefore key off the first catalog tile for each source — all
 * sub-types of a source share the same category/icon — and fall back to an explicit definition for
 * sources that have no Create tile (e.g. the display-only {@link ListSourceType.LEAD_DATABASE}).
 */
export interface SourceFilterEntry {
	id: ListSourceType;
	/** i18n key (full path) for the short filter label. */
	labelKey: string;
	/** i18n key (full path) for the one-line description shown in the palette. */
	descriptionKey: string;
	/** Primary Create-flow category — used to group the palette. */
	category: CatalogCategory;
	/** Drives the icon-tile colour, reusing the Create catalog's {@link iconVariantClasses}. */
	iconVariant: CatalogIconVariant;
	icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
	secondIcon?: IconDefinition;
	/** Curated search corpus (from the Create tile) — label/description are matched separately. */
	keywords: string;
	/**
	 * Whether this source is offered as a selectable option in the filter palette. Display-only
	 * sources (legacy types kept resolvable for existing lists' icon/label but not offered as a
	 * filter) set this to `false`. Defaults to `true`.
	 */
	filterable: boolean;
}

interface SourceFilterSeed {
	id: ListSourceType;
	labelKey: string;
	descriptionKey: string;
	/** Display-only legacy source: resolvable via {@link getSourceFilterEntry} but hidden from the palette. */
	filterable?: boolean;
	/** Only used when the source has no Create-flow catalog tile. */
	fallbackCategory?: CatalogCategory;
	fallbackIconVariant?: CatalogIconVariant;
	fallbackSecondIcon?: IconDefinition;
	fallbackKeywords?: string;
}

const labelKey = (id: ListSourceType) => `list.sourceFilter.sources.${id}.label`;
const descriptionKey = (id: ListSourceType) => `list.sourceFilter.sources.${id}.description`;

const seed = (id: ListSourceType, fallback: Partial<SourceFilterSeed> = {}): SourceFilterSeed => ({
	id,
	labelKey: labelKey(id),
	descriptionKey: descriptionKey(id),
	...fallback,
});

/**
 * The filterable sources, in registry order. Grouping/ordering for display is applied later via
 * {@link getSourceFilterGroups}; this list only needs to enumerate every source the overview can
 * filter by (it mirrors the previous inline `sourceTypesList`, minus the "ALL" pseudo-entry).
 */
const SOURCE_FILTER_SEEDS: SourceFilterSeed[] = [
	seed(ListSourceType.LI_SN),
	seed(ListSourceType.LI_PEOPLE),
	seed(ListSourceType.LI_EVENTS),
	seed(ListSourceType.LI_POST),
	seed(ListSourceType.LI_COMPANY_ACTIVITY),
	seed(ListSourceType.LI_PROFILE_VISITS),
	seed(ListSourceType.CSV_ENRICHMENTS),
	seed(ListSourceType.CUSTOM_EVENTS),
	// Display-only legacy source: COMPANY_ENRICH (relabelled "Lead Database") now covers the
	// database flow, so LEAD_DATABASE is hidden from the filter palette (`filterable: false`). It is
	// still seeded — with explicit fallbacks — so existing LEAD_DATABASE lists resolve an icon/label
	// via `getSourceFilterEntry`.
	seed(ListSourceType.LEAD_DATABASE, {
		filterable: false,
		fallbackCategory: CatalogCategory.DATABASE,
		fallbackIconVariant: CatalogIconVariant.DATABASE,
		fallbackSecondIcon: faDatabase,
		fallbackKeywords: "lead database contacts firmographic search leads",
	}),
	seed(ListSourceType.COMPANY_ENRICH),
	seed(ListSourceType.MAPS),
	seed(ListSourceType.PARALLEL_AI),
	seed(ListSourceType.JOBS),
	seed(ListSourceType.JOB_CHANGE),
	seed(ListSourceType.FUNDING_ROUNDS),
	seed(ListSourceType.TRACKER),
	seed(ListSourceType.SMART_LIST),
];

/** Category order/labels mirror the Create-flow rail ({@link CatalogCategory}). */
const CATEGORY_ORDER: CatalogCategory[] = [
	CatalogCategory.UPLOAD,
	CatalogCategory.SCRAPING,
	CatalogCategory.LINKEDIN,
	CatalogCategory.DATABASE,
	CatalogCategory.JOBS,
	CatalogCategory.FUNDING,
	CatalogCategory.SIGNALS,
	CatalogCategory.REENGAGE,
	CatalogCategory.EMPTY,
];

const catalogTiles = getCatalogTiles(createNewListSettings);

/** First Create-flow tile for a source (all sub-types share category/icon), if any. */
const tileForSource = (id: ListSourceType) => catalogTiles.find((tile) => tile.listType === id);

const buildEntry = (seedEntry: SourceFilterSeed): SourceFilterEntry => {
	const tile = tileForSource(seedEntry.id);

	return {
		id: seedEntry.id,
		labelKey: seedEntry.labelKey,
		descriptionKey: seedEntry.descriptionKey,
		category: tile?.categories[0] ?? seedEntry.fallbackCategory ?? CatalogCategory.DATABASE,
		iconVariant: tile?.iconVariant ?? seedEntry.fallbackIconVariant ?? CatalogIconVariant.DATABASE,
		icon: tile?.icon,
		secondIcon: tile?.secondIcon ?? seedEntry.fallbackSecondIcon,
		keywords: tile?.keywords ?? seedEntry.fallbackKeywords ?? "",
		filterable: seedEntry.filterable ?? true,
	};
};

let cachedEntries: SourceFilterEntry[] | null = null;

/** All filterable source entries, enriched from the Create catalog. Memoised. */
export const getSourceFilterEntries = (): SourceFilterEntry[] => {
	if (!cachedEntries) {
		cachedEntries = SOURCE_FILTER_SEEDS.map(buildEntry);
	}

	return cachedEntries;
};

export const getSourceFilterEntry = (id: string): SourceFilterEntry | undefined =>
	getSourceFilterEntries().find((entry) => entry.id === id);

export interface SourceFilterGroup {
	category: CatalogCategory;
	/** i18n key (full path) for the category header — reuses the Create-flow rail labels. */
	labelKey: string;
	entries: SourceFilterEntry[];
}

/**
 * Entries grouped by primary category, in {@link CATEGORY_ORDER}; empty groups are dropped.
 * Display-only sources ({@link SourceFilterEntry.filterable} === false) are excluded — they remain
 * resolvable via {@link getSourceFilterEntry} for existing lists but are never offered as a filter.
 */
export const getSourceFilterGroups = (): SourceFilterGroup[] => {
	const entries = getSourceFilterEntries().filter((entry) => entry.filterable);

	return CATEGORY_ORDER.map((category) => ({
		category,
		labelKey: `list.create.catalog.rail.categories.${category}`,
		entries: entries.filter((entry) => entry.category === category),
	})).filter((group) => group.entries.length > 0);
};

export const getSourceIconClasses = (variant: CatalogIconVariant): string =>
	iconVariantClasses[variant];

/**
 * Whether an entry matches a free-text query. Matches the label, description and curated keywords —
 * the label/description are resolved by the caller (it owns the i18n `t`) and passed in.
 */
export const sourceMatchesQuery = (
	entry: SourceFilterEntry,
	resolvedLabel: string,
	resolvedDescription: string,
	query: string
): boolean => {
	const normalized = query.trim().toLowerCase();

	if (!normalized) {
		return true;
	}

	return [resolvedLabel, resolvedDescription, entry.keywords]
		.join(" ")
		.toLowerCase()
		.includes(normalized);
};
